import { RENTAL_STRUCTURE_DISPLAY_NAME_DICT } from './enums';
import type {
  RentalStructure,
  RentalStructureOptionConfig,
} from '../types/configuration';

export interface ResolvedRentalStructureOption {
  key: string;
  label: string;
}

/**
 * Normalizes rental_structure_options from either format into a consistent
 * ResolvedRentalStructureOption[] array:
 *
 * - New format (array):  [{ key: 'adu', label?: 'ADU' }, ...]
 * - Legacy format (dict): { adu: true, apartment: false, ... }
 *
 * Labels fall back to RENTAL_STRUCTURE_DISPLAY_NAME_DICT when not provided.
 */
export function resolveRentalStructureOptions(
  options: RentalStructureOptionConfig[] | RentalStructure | undefined | null
): ResolvedRentalStructureOption[] {
  if (!options) return [];

  if (Array.isArray(options)) {
    return options.map(({ key, label }) => ({
      key,
      label:
        label ??
        RENTAL_STRUCTURE_DISPLAY_NAME_DICT[
          key as keyof typeof RENTAL_STRUCTURE_DISPLAY_NAME_DICT
        ] ??
        key,
    }));
  }

  // Legacy object format: { key: boolean } — filter out disabled entries
  return Object.entries(options)
    .filter(([, enabled]) => enabled)
    .map(([key]) => ({
      key,
      label:
        RENTAL_STRUCTURE_DISPLAY_NAME_DICT[
          key as keyof typeof RENTAL_STRUCTURE_DISPLAY_NAME_DICT
        ] ?? key,
    }));
}

/**
 * Returns the display label for a given rental structure key.
 * Looks up the resolved options first, then falls back to RENTAL_STRUCTURE_DISPLAY_NAME_DICT.
 */
export function getLabelByKey(
  key: string | undefined | null,
  options: ResolvedRentalStructureOption[]
): string | undefined {
  if (!key) return undefined;
  return (
    options.find((opt) => opt.key === key)?.label ??
    RENTAL_STRUCTURE_DISPLAY_NAME_DICT[
      key as keyof typeof RENTAL_STRUCTURE_DISPLAY_NAME_DICT
    ]
  );
}
